import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { addressService } from '../services/address';
import { PickupAddress } from '../types/address';

// Query Keys
const addressKeys = {
  all: ['address'] as const,
  pickup: (userId: string) => [...addressKeys.all, userId] as const,
};

export const useAddress = (userId: string) => {
  const queryClient = useQueryClient();

  // 🔹 GET addresses
  const query = useQuery({
    queryKey: addressKeys.pickup(userId),
    queryFn: () => addressService.fetch(userId),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000,
    retry: (count, error: any) => {
      if (error?.response?.status === 401) return false;
      return count < 2;
    },
  });

  // 🔹 ADD / UPDATE address
  const mutation = useMutation({
    mutationFn: (payload: PickupAddress) =>
      addressService.save(userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: addressKeys.pickup(userId),
      });
    },
  });

  // 🔹 UPDATE address
  const updateMutation = useMutation({
    mutationFn: ({
      pickupId,
      payload,
    }: {
      pickupId: string | number;
      payload: PickupAddress;
    }) => addressService.update(userId, pickupId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: addressKeys.pickup(userId),
      });
    },
  });

  // 🔹 DELETE address
  const deleteMutation = useMutation({
    mutationFn: (pickupId: string | number) =>
      addressService.delete(userId, pickupId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: addressKeys.pickup(userId),
      });
    },
  });

  return {
    ...query,
    saveAddress: mutation.mutate,
    saveAddressAsync: mutation.mutateAsync,
    isSaving: mutation.isPending,
    updateAddress: updateMutation.mutate,
    updateAddressAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteAddress: deleteMutation.mutate,
    deleteAddressAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
};
